import { useBooleanFlagValue } from '@openfeature/react-sdk';
import Button from '@risksmart-app/components/src/button';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Modal from '@risksmart-app/components/src/modal';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTRPC } from 'src/utils/trpc';
import { z } from 'zod';

import { useUpsertUserTablePreferences } from '@/hooks/mutations/user-table-preferences';
// Direct module import (not the @/hooks/queries barrel) — the barrel's import
// chain pulls every query hook and their generated GraphQL documents into
// this login-critical component's graph.
import { useGetUserTablePreferences } from '@/hooks/queries/user-table-preferences/useGetUserTablePreferences';

/**
 * Dedicated user_table_preferences key holding the per-user dismissal state.
 * Actor-pinned server-side, so the 30-day cooldown follows the user across
 * devices (RSP-6610 AC).
 */
const G2_REVIEW_PROMPT_TABLE_ID = 'g2_review_prompt';

const DISMISS_COOLDOWN_DAYS = 30;
const DISMISS_COOLDOWN_MS = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

/**
 * Top-level org roles that may see the prompt. Mirrors the server-side gate
 * in packages/trpc's g2-review.service — the server enforces independently.
 */
const ELIGIBLE_ROLES = ['RiskManager', 'StandardEnhanced'];

const dismissalPreferencesSchema = z.object({
  dismissedAt: z.string().optional(),
});

/**
 * Whether the stored dismissal (if any) still suppresses the prompt.
 * Compares the client clock against a client-written timestamp — a skewed
 * device clock can shorten or stretch the cooldown. Accepted trade-off of a
 * client-owned cooldown; the server-side already-reviewed check is the
 * authoritative backstop against re-prompting reviewers.
 */
const isInDismissCooldown = (preferences: unknown): boolean => {
  const parsed = dismissalPreferencesSchema.safeParse(preferences);
  if (!parsed.success || !parsed.data.dismissedAt) {
    return false;
  }
  const dismissedAt = Date.parse(parsed.data.dismissedAt);

  return (
    !Number.isNaN(dismissedAt) && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS
  );
};

/**
 * Login-time G2 review prompt (RSP-6610). Mounted once in ProtectedLayout;
 * renders nothing unless every gate passes:
 *
 *  1. `g2_review_prompt` org feature flag (client-side; server re-checks);
 *  2. caller role is RiskManager / StandardEnhanced (ditto);
 *  3. no dismissal within the last 30 days (user_table_preferences);
 *  4. the server's `g2Review.promptState` says eligible — which also covers
 *     the "already reviewed on G2" check and mints the iframe URL.
 *
 * "Maybe later" (button, close, backdrop) stores a dismissal timestamp so
 * the prompt stays hidden for 30 days across devices. Every failure path
 * renders nothing — login is never blocked or errored by this component.
 */
export const G2ReviewPrompt = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'g2_review' });
  const trpc = useTRPC();
  const { user } = useRisksmartUser();
  const flagEnabled = useBooleanFlagValue('g2_review_prompt', false);
  const [dismissed, setDismissed] = useState(false);

  const roleEligible = ELIGIBLE_ROLES.includes(user?.orgRole ?? '');

  const {
    data: preferencesData,
    loading: preferencesLoading,
    error: preferencesError,
  } = useGetUserTablePreferences({
    queryArgs: { tableId: G2_REVIEW_PROMPT_TABLE_ID },
    shouldSkip: !flagEnabled || !roleEligible,
  });
  const preferences = preferencesData?.user_table_preferences[0]?.Preferences;
  const inCooldown = isInDismissCooldown(preferences);

  // Fail closed on a preferences fetch error: without the stored dismissal
  // we can't rule out an active cooldown, and re-prompting a user who just
  // dismissed is worse than skipping a login.
  const promptStateEnabled =
    flagEnabled &&
    roleEligible &&
    !preferencesLoading &&
    !preferencesError &&
    !inCooldown;
  const { data: promptState } = useQuery(
    trpc.frontend.g2Review.promptState.queryOptions(undefined, {
      enabled: promptStateEnabled,
      // One shot per login-time mount: the eligibility answer won't change
      // mid-session, and a failed G2 call should hide the prompt, not retry.
      retry: false,
      staleTime: Infinity,
    })
  );

  const { upsertUserTablePreferences } = useUpsertUserTablePreferences();

  const handleDismiss = () => {
    setDismissed(true);
    // Fire-and-forget: a failed write only means the prompt may reappear on
    // a later login, which is not worth surfacing an error for.
    upsertUserTablePreferences({
      TableId: G2_REVIEW_PROMPT_TABLE_ID,
      Preferences: { dismissedAt: new Date().toISOString() },
    }).catch((error: unknown) => {
      console.warn('Failed to store G2 review prompt dismissal', error);
    });
  };

  if (!promptStateEnabled || dismissed || !promptState?.eligible) {
    return null;
  }

  return (
    <Modal
      visible={true}
      size={'large'}
      header={t('title')}
      onDismiss={handleDismiss}
    >
      <p className={'mb-4'}>{t('description')}</p>
      {/*
        sandbox omits allow-top-navigation so a compromised embed can't
        redirect the app tab; popups are allowed for G2's own login flows.
        Validate against the live embed during the per-org flag rollout.
        strict-origin stops the app's path (tenant/entity segments) leaking
        to G2 in the Referer.
      */}
      <iframe
        src={promptState.iframeUrl}
        title={t('iframe_title')}
        className={'w-full border-0'}
        style={{ height: '60vh' }}
        sandbox={
          'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
        }
        referrerPolicy={'strict-origin'}
      />
      <div className={'mt-4 flex justify-end'}>
        <Button onClick={handleDismiss}>{t('maybe_later')}</Button>
      </div>
    </Modal>
  );
};

export default G2ReviewPrompt;
